import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import EsitoBadge from "../components/EsitoBadge";
import { HouseIconSmall, HouseIconEmpty } from "../components/HouseIcons";
import api, { API } from "../lib/api";

export default function Landing() {
  const [preview, setPreview] = useState([]);
  useEffect(() => {
    api.get("/public/properties").then((r) => setPreview((r.data || []).slice(0, 3))).catch(() => {});
  }, []);
  const photoUrl = (p) =>
    p?.photos?.[0]?.external_url ||
    (p?.photos?.[0]?.path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.photos[0].path}` : null);

  return (
    <div className="paper-bg" data-testid="landing-page">
      <PublicNav />

      {/* HERO — Due tipi di immobili */}
      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
        {/* Lockup: bollino + nome brand */}
        <div className="flex items-center gap-4 mb-8" data-testid="hero-lockup">
          <img
            src="/brand/bollino-512.png"
            alt="Bollino Pronto alla Vendita"
            className="w-14 h-14 md:w-16 md:h-16 shrink-0"
          />
          <span className="font-serif text-2xl md:text-3xl text-[#7D2E3F] tracking-[0.12em]">
            PRONTO ALLA VENDITA
          </span>
        </div>

        {/* Titolo (protagonista) */}
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight text-[#3A3D42] max-w-4xl">
          Da oggi il mercato ha<br/>due tipi di immobili.
        </h1>

        {/* Sottotitolo grigio, sotto al titolo */}
        <p className="text-[#5C5657] text-sm md:text-base mt-6 max-w-2xl" data-testid="hero-subtitle">
          La categoria degli immobili verificati e valorizzati prima del mercato.
        </p>

        <div className="grid md:grid-cols-2 gap-8 mt-16">
          <div className="border border-[#E6E4E0] p-10 bg-white" data-testid="card-standard">
            <HouseIconEmpty className="w-16 h-16" />
            <h3 className="font-serif text-3xl mt-8">Immobile in vendita</h3>
            <p className="text-[#5C5657] mt-4 leading-relaxed">
              Entra sul mercato con il grado di preparazione che ciascun operatore sceglie di dargli. Non esiste
              uno standard: quello che emerge dopo, emerge in trattativa.
            </p>
          </div>
          <div className="border border-[#7D2E3F] p-10 bg-[#FDFDFC] shadow-[0_1px_0_#7D2E3F]" data-testid="card-pronto">
            <img
              src="/brand/bollino-512.png"
              alt="Bollino Pronto alla Vendita"
              className="w-16 h-16"
              data-testid="card-pronto-bollino"
            />
            <h3 className="font-serif text-3xl mt-8 text-[#7D2E3F]">Immobile Pronto alla Vendita</h3>
            <p className="text-[#3A3D42] mt-4 leading-relaxed">
              Arriva sul mercato già in ordine: criticità note e quantificate, interventi di valorizzazione
              individuati. Il prezzo non si difende: si spiega.
            </p>
          </div>
        </div>

        <div className="mt-12">
          <Link to="/form" data-testid="hero-cta-form" className="btn-primary">Scopri se la tua casa può entrare</Link>
        </div>
      </section>

      {/* COME FUNZIONA — Non un check tecnico, due movimenti */}
      <section id="come-funziona" className="border-y border-[#E6E4E0] paper-alt-bg" data-testid="section-come-funziona">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
          <div className="label-uppercase mb-4">Come nasce la categoria</div>
          <h2 className="font-serif text-4xl md:text-5xl max-w-3xl mb-6">
            Due movimenti. Un'unica categoria.
          </h2>
          <p className="text-[#5C5657] max-w-2xl mb-16 leading-relaxed">
            Non è un audit né una perizia. È una fotografia dello stato dell'immobile,
            seguita da un piano che lo prepara al mercato. La verifica racconta la realtà.
            La valorizzazione trasforma la realtà in valore.
          </p>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { n: "01", t: "Verifica", d: "Il PTI e le aziende specializzate accertano lo stato dell'immobile. Non un giudizio: una fotografia documentale e tecnica dei fatti — proprietà, urbanistica, catasto, agibilità, condominio, stato conservativo, i quattro impianti." },
              { n: "02", t: "Valorizzazione", d: "Il differenziale del Protocollo. RX CASA quantifica ogni criticità — costo, tempi, a carico di chi — e indica gli interventi che restituiscono valore. Non promettiamo un immobile perfetto: consegniamo un immobile pronto." },
              { n: "03", t: "Categoria", d: "L'immobile riceve l'esito e, se positivo, entra nella Galleria Pronto alla Vendita. Non un bollino di qualità: una categoria di mercato con regole trasparenti, riconoscibili, sottoscritte." },
            ].map((s) => (
              <div key={s.n} className="pt-8 border-t border-[#3A3D42]">
                <div className="font-serif text-6xl text-[#7D2E3F]">{s.n}</div>
                <h3 className="font-serif text-2xl mt-3">{s.t}</h3>
                <p className="text-sm text-[#5C5657] mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALORIZZAZIONE — Cuore della categoria */}
      <section className="max-w-[1240px] mx-auto px-6 md:px-10 py-24" data-testid="section-valorizzazione">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="label-uppercase mb-4">Il differenziale</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              La verifica racconta.<br/>La <span className="italic text-[#7D2E3F]">valorizzazione</span> agisce.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-8">
            <p className="text-[#3A3D42] leading-relaxed text-lg drop-cap">
              Sapere quali sono le criticità è il minimo. Il Protocollo indica anche <span className="text-[#7D2E3F]">quali interventi
              restituiscono valore</span> in trattativa — quali si fanno prima della messa sul mercato,
              quali si dichiarano, quali si scaricano sul prezzo.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border-t border-[#7D2E3F] pt-4">
                <div className="label-uppercase text-[#7D2E3F]">Per il Venditore</div>
                <p className="text-sm mt-3 text-[#3A3D42] leading-relaxed">
                  Un piano di valorizzazione con costi e tempi, non un elenco di problemi.
                  L'immobile arriva sul mercato dove serve arrivare — non dove capita.
                </p>
              </div>
              <div className="border-t border-[#7D2E3F] pt-4">
                <div className="label-uppercase text-[#7D2E3F]">Per l'Acquirente</div>
                <p className="text-sm mt-3 text-[#3A3D42] leading-relaxed">
                  Nessuna sorpresa dopo il rogito. Le criticità Rilevanti sono dichiarate e
                  sottoscritte prima della proposta. La trattativa si fa sui fatti.
                </p>
              </div>
              <div className="border-t border-[#7D2E3F] pt-4">
                <div className="label-uppercase text-[#7D2E3F]">Per il Prezzo</div>
                <p className="text-sm mt-3 text-[#3A3D42] leading-relaxed">
                  Non si difende: si spiega. Il valore chiesto è motivato per iscritto — cosa è
                  stato fatto, cosa resta, cosa vale.
                </p>
              </div>
              <div className="border-t border-[#7D2E3F] pt-4">
                <div className="label-uppercase text-[#7D2E3F]">Per il Mercato</div>
                <p className="text-sm mt-3 text-[#3A3D42] leading-relaxed">
                  Una nuova categoria riconoscibile — immobili preparati, non solo esposti.
                  Con regole scritte e un garante del Protocollo che le custodisce.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VETRINA GALLERIA — anteprima 3 immobili */}
      {preview.length > 0 && (
        <section className="border-y border-[#E6E4E0]" data-testid="section-vetrina-galleria">
          <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div>
                <div className="label-uppercase mb-3">La vetrina della categoria</div>
                <h2 className="font-serif text-4xl md:text-5xl leading-tight max-w-2xl">
                  Immobili già pronti al mercato.
                </h2>
              </div>
              <Link
                to="/galleria"
                data-testid="landing-vetrina-cta"
                className="text-xs label-uppercase text-[#7D2E3F] hover:underline self-start md:self-end"
              >
                Vedi tutta la Galleria →
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
              {preview.map((p) => (
                <Link
                  to={`/immobile/${p.id}`}
                  key={p.id}
                  data-testid={`landing-preview-${p.id}`}
                  className="group flex flex-col border border-transparent hover:border-[#E6E4E0] transition-colors"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-[#F4F3F1] border border-[#E6E4E0]">
                    {photoUrl(p) ? (
                      <img
                        src={photoUrl(p)}
                        alt={p.titolo}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#5C5657]">
                        Nessuna foto
                      </div>
                    )}
                    {p.stato_pubblicazione === "venduto" ? (
                      <span className="absolute top-3 right-3 bg-[#3A3D42] text-white text-xs tracking-[0.2em] uppercase px-3 py-1 shadow-sm">
                        Venduto
                      </span>
                    ) : (
                      <>
                        {(p.esito === "pronto" || p.esito === "criticita") && (
                          <img
                            src="/brand/bollino-512.png"
                            alt="Pronto alla Vendita"
                            title={p.esito === "criticita" ? "Pronto alla Vendita con Criticità Dichiarate" : "Pronto alla Vendita"}
                            className="absolute top-3 right-3 w-12 h-12 md:w-14 md:h-14 [filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.22))_drop-shadow(0_6px_14px_rgba(0,0,0,0.28))]"
                            data-testid={`landing-bollino-${p.id}`}
                          />
                        )}
                        <span className="absolute bottom-3 left-3 bg-white/90 text-[#3A3D42] text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border border-[#E6E4E0]">
                          Disponibile
                        </span>
                      </>
                    )}
                  </div>
                  <div className="mt-5">
                    <div className="label-uppercase">{p.tipologia} · {p.comune}</div>
                    <h3 className="font-serif text-2xl mt-1 leading-tight group-hover:text-[#7D2E3F]">
                      {p.titolo}
                    </h3>
                    <div className="text-sm text-[#5C5657] mt-2">
                      {p.superficie} m² · {p.locali} locali
                    </div>
                    <div className="mt-4"><EsitoBadge esito={p.esito} /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* I 3 ESITI */}
      <section className="paper-alt-bg border-y border-[#E6E4E0]" data-testid="section-esiti">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="label-uppercase mb-4">Gli esiti</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">L'immobile entra in categoria. O resta fuori.</h2>
              <p className="text-sm text-[#5C5657] mt-6 leading-relaxed">
                Non un voto sull'immobile: una collocazione di mercato.
                L'esito indica se l'immobile può essere immesso nella categoria Pronto alla Vendita
                — con o senza criticità dichiarate — o se non è ancora pronto per entrarci.
              </p>
            </div>
            <div className="md:col-span-7 space-y-6">
              <div className="border border-[#E6E4E0] p-8 bg-white">
                <span className="badge-stamp badge-pronto">Pronto alla Vendita</span>
                <p className="text-sm text-[#3A3D42] mt-4 leading-relaxed">Entra in categoria a pieno titolo: non risultano criticità da dichiarare sulla base delle informazioni raccolte.</p>
              </div>
              <div className="border border-[#E6E4E0] p-8 bg-white">
                <span className="badge-stamp badge-criticita">Pronto alla Vendita con Criticità Dichiarate</span>
                <p className="text-sm text-[#3A3D42] mt-4 leading-relaxed">Entra in categoria con le criticità Rilevanti quantificate e sottoscritte dall'Acquirente prima della proposta.</p>
              </div>
              <div className="border border-[#E6E4E0] p-8 bg-white">
                <span className="badge-stamp badge-non_conforme">Non Conforme al Protocollo RX CASA</span>
                <p className="text-sm text-[#3A3D42] mt-4 leading-relaxed">Non entra in categoria — un vizio insanabile ne impedisce oggi il trasferimento. Il Venditore riceve un report separato con le strade percorribili.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOLLINO — Segno di esito unico */}
      <section className="border-b border-[#E6E4E0]" data-testid="section-bollino">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 flex justify-center">
            <div className="border border-[#E6E4E0] p-12 bg-[#FDFDFC]">
              <img
                src="/brand/bollino-512.png"
                alt="Bollino Pronto alla Vendita by RX CASA"
                className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56"
                data-testid="bollino-official"
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="label-uppercase mb-4">Il Segno di esito</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Un segno. Una categoria.</h2>
            <p className="text-[#5C5657] mt-6 leading-relaxed max-w-xl">
              Il Segno di esito è la firma visiva della categoria. Compare sulla copertina del Fascicolo e,
              sugli annunci delle Agenzie Partner, prende il nome di <span className="text-[#3A3D42]">Bollino Pronto alla Vendita by RX CASA</span>.
              Chi lo espone non promette la perfezione: dichiara di aver applicato il Protocollo.
            </p>
          </div>
        </div>
      </section>

      {/* CTA FORM */}
      <section className="paper-alt-bg border-y border-[#E6E4E0]">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Pensi che la tua casa possa entrare in categoria?</h2>
            <p className="text-[#5C5657] mt-6 max-w-xl leading-relaxed">
              Raccontacela in due minuti. Ti mettiamo in contatto con l'Agenzia Partner della tua zona.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <Link to="/form" data-testid="footer-cta-form" className="btn-primary">Scopri se la tua casa può entrare</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
